import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, User, Phone,  MapPin, GraduationCap, Calendar, Heart, AlertTriangle, Award, Edit, BookOpen, Stethoscope, ShieldCheck } from "lucide-react";


interface StudentProfileProps {
  student: any;
  onBack: () => void;
}

const StudentProfile = ({ student, onBack }: StudentProfileProps) => {
  return (
    <div className="w-full">
            <div className="mb-6">
              <Button 
                variant="ghost" 
                onClick={onBack} 
                className="mb-4 flex items-center gap-2 text-gray-900 dark:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Students
              </Button>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Student Profile</h1>
                  <p className="text-gray-600 dark:text-gray-400">Complete student information and records</p>
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Profile Overview */}
              <div className="lg:col-span-1">
                <Card className="border-none bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50">
                  <CardHeader className="text-center">
                    {student.photoUrl ? (
                      <img 
                        src={student.photoUrl} 
                        alt={`${student.firstName} ${student.lastName}`} 
                        className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-lg mx-auto mb-4 bg-gray-100 dark:bg-gray-800"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white dark:border-gray-800 shadow-lg">
                        <span className="text-2xl font-bold text-white">
                          {student.firstName[0]}{student.lastName[0]}
                        </span>
                      </div>
                    )}
                    <CardTitle className="text-xl text-gray-900 dark:text-white">{student.firstName} {student.middleName} {student.lastName}</CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-400">{student.registrationNumber}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-center">
                      <Badge 
                        variant={student.status === "Active" ? "default" : "destructive"} 
                        className="px-4 py-1 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {student.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-blue-500" />
                        <span className="text-gray-600 dark:text-gray-400">Grade:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{student.grade}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-green-500" />
                        <span className="text-gray-600 dark:text-gray-400">Class:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{student.class}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-purple-500" />
                        <span className="text-gray-600 dark:text-gray-400">Dormitory:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{student.dormitory}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-orange-500" />
                        <span className="text-gray-600 dark:text-gray-400">Date of Birth:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{new Date(student.dateOfBirth).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">Quick Stats</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Behavior Score</span>
                          <span className={`text-sm font-medium ${student.behaviorScore >= 80 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                            {student.behaviorScore}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Attendance</span>
                          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{student.attendance}%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Information */}
              <div className="lg:col-span-2">
                <Tabs defaultValue="personal" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 bg-gray-100 dark:bg-gray-800">
                    <TabsTrigger value="personal" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                      <User className="h-4 w-4" />
                      <span className="hidden sm:inline">Personal</span>
                    </TabsTrigger>
                    <TabsTrigger value="academic" className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white">
                      <BookOpen className="h-4 w-4" />
                      <span className="hidden sm:inline">Academic</span>
                    </TabsTrigger>
                    <TabsTrigger value="medical" className="flex items-center gap-2 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                      <Stethoscope className="h-4 w-4" />
                      <span className="hidden sm:inline">Medical</span>
                    </TabsTrigger>
                    <TabsTrigger value="behavior" className="flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <ShieldCheck className="h-4 w-4" />
                      <span className="hidden sm:inline">Behavior</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="personal" className="space-y-6">
                    <Card className="border-none">
                      <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50">
                        <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <User className="h-5 w-5 text-blue-600" />
                          Personal Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">First Name</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.firstName}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Middle Name</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.middleName}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Name</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.lastName}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Gender</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.gender}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Date of Birth</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{new Date(student.dateOfBirth).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Registration Number</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.registrationNumber}</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-none">
                      <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/50">
                        <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <Phone className="h-5 w-5 text-green-600" />
                          Parent/Guardian Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Parent Name</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.parentName}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone Number</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.parentPhone}</p>
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email Address</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{(student.parentName || "parent").replace(/\s+/g, '').toLowerCase()}@email.com</p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="academic" className="space-y-6">
                    <Card className="border-none">
                      <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/50">
                        <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <GraduationCap className="h-5 w-5 text-green-600" />
                          Academic Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Grade</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.grade}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Class</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.class}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Dormitory</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.dormitory}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Attendance Rate</label>
                          <p className="mt-1 text-gray-900 dark:text-white">{student.attendance}%</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-none">
                      <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50">
                        <CardTitle className="text-gray-900 dark:text-white">Recent Grades</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-2">
                          {['Mathematics', 'English', 'Science', 'History', 'Physical Education'].map((subject, index) => (
                            <div key={subject} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{subject}</span>
                              <Badge variant="outline" className="text-gray-900 dark:text-white border-gray-300 dark:border-gray-600">{85 + index * 2}%</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="medical" className="space-y-6">
                    <Card className="border-none">
                      <CardHeader className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-950/50 dark:to-red-900/50">
                        <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <Heart className="h-5 w-5 text-red-600" />
                          Medical Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Medical Conditions</label>
                            <p className="mt-1 text-gray-900 dark:text-white">{student.medicalInfo}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Emergency Contact</label>
                            <p className="mt-1 text-gray-900 dark:text-white">{student.parentName} - {student.parentPhone}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Blood Type</label>
                            <p className="mt-1 text-gray-900 dark:text-white">A+</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="behavior" className="space-y-6">
                    <Card className="border-none">
                      <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950/50 dark:to-purple-900/50">
                        <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <Award className="h-5 w-5 text-purple-600" />
                          Behavior & Discipline
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-white">Overall Behavior Score</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Based on recent behavior reports</p>
                            </div>
                            <div className={`text-2xl font-bold ${student.behaviorScore >= 80 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                              {student.behaviorScore}%
                            </div>
                          </div>

                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 dark:text-white">Recent Incidents</h4>
                            {student.behaviorScore < 80 ? (
                              <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-800 rounded">
                                <AlertTriangle className="h-4 w-4 text-orange-500" />
                                <span className="text-sm text-gray-900 dark:text-white">Late to class - March 15, 2024</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 rounded">
                                <Award className="h-4 w-4 text-green-500" />
                                <span className="text-sm text-gray-900 dark:text-white">Exemplary behavior - No recent incidents</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
    </div>
  );
};

export default StudentProfile;